import React, { useCallback, useEffect, useState } from 'react'

import { observer } from 'mobx-react-lite'

import { ReactComponent as StudentIcon } from 'assets/icons/student.svg'

import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'

import { ReactComponent as PersonAddIcon } from 'assets/icons/person.svg'

import { ReactComponent as SearchIcon } from 'assets/icons/search.svg'

import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'

import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'

import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'

import Toast from 'shared/ui/Toast/Toast'

import Section from './components/Section/Section'

import OrganizationEmptyState from './components/OrganizationEmptyState/OrganizationEmptyState'

import Students from './components/Students/Students'

import Counselors from './components/Counselors/Counselors'

import SelectDropdown from 'shared/ui/SelectDropdown/SelectDropdown'

import { organizationContextStore } from 'shared/stores/organizationContextStore'

import { counselorSessionStore } from 'shared/stores/counselorSessionStore'

import { AddStudentFields, useAddStudentSearch } from './components/AddStudent/AddStudentFields'

import { useStudentsSection } from './hooks/useStudentsSection'

import { useCounselorsSection } from './hooks/useCounselorsSection'

import { assignSelectedStudents } from './model/assignStudentsApi'

import { unlinkSelectedStudents } from './model/unlinkStudentsApi'

import { linkStudentToOrganization } from './model/linkStudentsApi'

import {

	createCounselorInOrganization,

	removeCounselorFromOrganization,

	updateCounselorAccessLevel,

} from './model/counselorMutationsApi'

import { userManagementStore } from './model/userManagementStore'

import { counselorsStore } from './model/counselorsStore'

import s from './styles/UserManagementPage.module.scss'



export default observer(function UserManagementPage() {

	const [studentsOpen, setStudentsOpen] = useState(true)

	const [counselorsOpen, setCounselorsOpen] = useState(false)

	const [modal, setModal] = useState(null)

	const [showToast, setShowToast] = useState(false)

	const [toastMessage, setToastMessage] = useState('')

	const [isLinkingStudent, setIsLinkingStudent] = useState(false)

	const [isCreatingCounselor, setIsCreatingCounselor] = useState(false)

	const [studentCounselorId, setStudentCounselorId] = useState('')

	const { showErrorSafe } = useSafeErrorBanner()

	const [form, setForm] = useState({

		name: '',

		email: '',

		access: 'Counsellor',

	})



	const students = userManagementStore.students

	const counselors = userManagementStore.counselors

	const accessOptions = counselorsStore.accessOptions



	const handleStudentLookupError = useCallback(

		error => {

			showErrorSafe(

				error?.response?.data?.error ||

					error?.message ||

					'Failed to search for student.',

			)

		},

		[showErrorSafe],

	)



	const addStudentSearch = useAddStudentSearch({

		enabled: modal === 'student',

		onLookupError: handleStudentLookupError,

	})



	useEffect(() => {
		organizationContextStore.ensureInitialized().then(() => {
			if (organizationContextStore.isReady) {
				userManagementStore.ensureLoaded().catch(() => {})
			}
		}).catch(() => {})
	}, [])

	const handleOrganizationChange = useCallback(async organizationId => {
		if (!organizationId) {
			organizationContextStore.clearSelectedOrganization()
			userManagementStore.resetForOrganizationChange()
			return
		}
		const match = organizationContextStore.organizations.find(
			org => String(org.id) === String(organizationId),
		)
		organizationContextStore.setSelectedOrganization(organizationId, match?.name)
		userManagementStore.resetForOrganizationChange()
		await userManagementStore.ensureLoaded({ force: true })
	}, [])



	const [localStudents, setLocalStudents] = useState(null)



	useEffect(() => {

		setLocalStudents(null)

	}, [students])



	const displayStudents = localStudents ?? students

	const displayCounselors = counselors



	const setStudents = updater => {

		setLocalStudents(current => {

			const base = current ?? students

			return typeof updater === 'function' ? updater(base) : updater

		})

	}



	const refreshUserManagement = useCallback(async () => {

		setLocalStudents(null)

		await userManagementStore.ensureLoaded({ force: true })

	}, [])



	const handleAssignSelected = useCallback(

		async ({ counselor, studentIds }) => {

			const counselorId = counselor?.id

			if (!counselorId || !Array.isArray(studentIds) || !studentIds.length) {

				throw new Error('Missing counselor or students for assignment.')

			}



			try {

				await assignSelectedStudents({

					counselorId,

					studentIds,

				})

				await refreshUserManagement()

				setToastMessage(

					studentIds.length === 1

						? '1 student assigned.'

						: `${studentIds.length} students assigned.`,

				)

				setShowToast(true)

			} catch (error) {

				showErrorSafe(

					error?.response?.data?.error ||

						error?.message ||

						'Failed to assign students.',

				)

				throw error

			}

		},

		[showErrorSafe, refreshUserManagement],

	)



	const handleUnlinkStudents = useCallback(

		async ({ studentIds }) => {

			if (!Array.isArray(studentIds) || !studentIds.length) {

				throw new Error('Missing students for removal.')

			}



			try {

				await unlinkSelectedStudents({ studentIds })

				await refreshUserManagement()

				setToastMessage(

					studentIds.length === 1

						? '1 student removed from this school.'

						: `${studentIds.length} students removed from this school.`,

				)

				setShowToast(true)

			} catch (error) {

				showErrorSafe(

					error?.response?.data?.error ||

						error?.message ||

						'Failed to remove students.',

				)

				throw error

			}

		},

		[showErrorSafe, refreshUserManagement],

	)



	const handleUpdateCounselorAccess = useCallback(

		async ({ counselorId, accessLabel }) => {

			try {

				await updateCounselorAccessLevel({

					counselorId,

					accessLabel,

					roles: counselorsStore.roles,

				})

				await refreshUserManagement()

				setToastMessage('Counsellor access updated.')

				setShowToast(true)

			} catch (error) {

				showErrorSafe(

					error?.response?.data?.error ||

						error?.message ||

						'Failed to update counsellor access.',

				)

				throw error

			}

		},

		[refreshUserManagement, showErrorSafe],

	)



	const handleRemoveCounselor = useCallback(

		async ({ counselorId }) => {

			try {

				await removeCounselorFromOrganization(counselorId)

				await refreshUserManagement()

				setToastMessage('Counsellor removed from this organization.')

				setShowToast(true)

			} catch (error) {

				showErrorSafe(

					error?.response?.data?.error ||

						error?.message ||

						'Failed to remove counsellor.',

				)

				throw error

			}

		},

		[refreshUserManagement, showErrorSafe],

	)



	const studentsVm = useStudentsSection({

		students: displayStudents,

		counselors: displayCounselors,

		setStudents,

		onAssignSelected: handleAssignSelected,

		onUnlinkStudents: handleUnlinkStudents,

	})

	const counselorsVm = useCounselorsSection({

		counselors: displayCounselors,

		students: displayStudents,

		accessOptions,

		onUpdateAccess: handleUpdateCounselorAccess,

		onRemoveCounselor: handleRemoveCounselor,

	})



	const close = () => {

		if (isLinkingStudent || isCreatingCounselor) return

		setModal(null)

		setStudentCounselorId('')

		addStudentSearch.reset()

		setForm({

			name: '',

			email: '',

			access: 'Counsellor',

		})

	}



	const submit = async e => {

		e.preventDefault()

		if (modal === 'counselor') {

			const name = form.name.trim()

			const email = form.email.trim()

			if (!name || !email || isCreatingCounselor) return



			setIsCreatingCounselor(true)

			try {

				await createCounselorInOrganization({

					name,

					email,

					accessLabel: form.access,

					roles: counselorsStore.roles,

				})

				await refreshUserManagement()

				setToastMessage('Counsellor added to this organization.')

				setShowToast(true)

				close()

			} catch (error) {

				showErrorSafe(

					error?.response?.data?.error ||

						error?.message ||

						'Failed to add counsellor.',

				)

			} finally {

				setIsCreatingCounselor(false)

			}

			return

		}



		const studentId = addStudentSearch.selectedStudent?.id

		if (!studentId || !addStudentSearch.canSubmit || isLinkingStudent) return



		setIsLinkingStudent(true)

		try {

			await linkStudentToOrganization({

				studentId,

				counselorId: studentCounselorId,

			})

			await refreshUserManagement()

			setToastMessage('Student linked to this school.')

			setShowToast(true)

			close()

		} catch (error) {

			showErrorSafe(

				error?.response?.data?.error ||

					error?.message ||

					'Failed to link student.',

			)

		} finally {

			setIsLinkingStudent(false)

		}

	}



	const isLoading = userManagementStore.isLoading

	const counselorsLoading = counselorsStore.loadStatus === 'loading'
	const counselorsError =
		counselorsStore.loadStatus === 'error' ? counselorsStore.error : null
	const counselorsLoaded = counselorsStore.loadStatus === 'success'

	const loadError =
		userManagementStore.error ||
		organizationContextStore.error

	const organizationSelectOptions = organizationContextStore.organizations.map(org => ({
		value: String(org.id),
		label: org.name,
	}))

	const showOrganizationPicker =
		counselorSessionStore.isPlatformCeo &&
		organizationContextStore.organizations.length > 1 &&
		organizationContextStore.isReady

	const organizationBannerName =
		counselorSessionStore.isPlatformCeo && !organizationContextStore.isReady
			? null
			: organizationContextStore.selectedOrganizationName ||
				counselorSessionStore.profile?.organization?.name

	const waitingForOrganization =
		counselorSessionStore.isPlatformCeo && !organizationContextStore.isReady

	const counselorAccessOptions = accessOptions.map(option => ({

		value: option,

		label: option,

	}))



	return (

		<main className={s.page}>

			<header className={s.head}>

				<div className={s.headMain}>
					{counselorSessionStore.isPlatformCeo ? (
						<span className={s.headIcon} aria-hidden="true">
							<PeopleIcon />
						</span>
					) : null}
					<div className={s.headCopy}>
						<h1>User management</h1>
						<p>Assign students, manage counsellors and keep ownership clear.</p>
					</div>
					{organizationBannerName && !showOrganizationPicker ? (
						<p className={s.orgBanner}><span>Managing organization</span><strong>{organizationBannerName}</strong></p>
					) : null}

					{showOrganizationPicker ? (
						<div className={s.orgPicker}>
							<label>
								<span>Organization</span>
								<SelectDropdown
									value={
										organizationContextStore.selectedOrganizationId
											? String(organizationContextStore.selectedOrganizationId)
											: ''
									}
									onChange={handleOrganizationChange}
									options={organizationSelectOptions}
									placeholder="Select organization"
									menuZIndex={3000}
								/>
							</label>
						</div>
					) : null}
				</div>

			</header>



			{waitingForOrganization ? (
				<OrganizationEmptyState
					options={organizationSelectOptions}
					value={
						organizationContextStore.selectedOrganizationId
							? String(organizationContextStore.selectedOrganizationId)
							: ''
					}
					onChange={handleOrganizationChange}
					disabled={organizationContextStore.loadStatus === 'loading'}
				/>
			) : null}



			{loadError ? (

				<p role="alert" style={{ color: 'red', marginBottom: '1rem' }}>

					{loadError}

				</p>

			) : null}



			{!waitingForOrganization ? (
				<>
			<Section

				icon={StudentIcon}

				title="All students"

				subtitle="Manage and assign counselors to students."

				count={displayStudents.length}

				open={studentsOpen}

				onToggle={() => setStudentsOpen(x => !x)}

				leadingActions={

					<label className={s.headSearch} onClick={event => event.stopPropagation()}>

						<SearchIcon aria-hidden />

						<input

							type="search"

							value={studentsVm.search}

							onChange={e => studentsVm.setSearch(e.target.value)}

							placeholder="Search student"

						/>

					</label>

				}

				actions={

					<button type="button" className={s.primary} onClick={() => setModal('student')}>

						<PlusIcon className={s.primaryIcon} aria-hidden />

						Add student

					</button>

				}

			>

				{isLoading && !displayStudents.length ? (

					<p style={{ padding: '1rem' }}>Loading students…</p>

				) : (

					<Students students={displayStudents} counselors={displayCounselors} vm={studentsVm} />

				)}

			</Section>



			<Section

				icon={PeopleIcon}

				title="All counsellors"

				subtitle="Manage team access and see current student ownership."

				count={displayCounselors.length}

				open={counselorsOpen}

				onToggle={() => setCounselorsOpen(x => !x)}

				leadingActions={

					<label className={s.headSearch} onClick={event => event.stopPropagation()}>

						<SearchIcon aria-hidden />

						<input

							type="search"

							value={counselorsVm.search}

							onChange={e => counselorsVm.setSearch(e.target.value)}

							placeholder="Search counsellor"

						/>

					</label>

				}

				actions={

					<button type="button" className={s.primary} onClick={() => setModal('counselor')}>

						<PlusIcon className={s.primaryIcon} aria-hidden />

						Add counsellor

					</button>

				}

			>

				{counselorsLoading ? (

					<p className={s.sectionMessage}>Loading counsellors…</p>

				) : counselorsError ? (

					<p className={s.sectionError} role="alert">

						{counselorsError}

					</p>

				) : counselorsLoaded ? (

					<Counselors
						vm={counselorsVm}
						isSourceEmpty={displayCounselors.length === 0}
					/>

				) : null}

			</Section>
				</>
			) : null}



			{modal && (

				<div className={s.backdrop} onMouseDown={close}>

					<form className={s.dialog} onSubmit={submit} onMouseDown={e => e.stopPropagation()}>

						<button type="button" className={s.dialogClose} onClick={close} aria-label="Close">

							<CrossIcon aria-hidden />

						</button>

						<div className={s.dialogHeader}>
							<span className={s.dialogIcon}>

								<PersonAddIcon aria-hidden />

							</span>
							<div>
								<h2>Add {modal === 'counselor' ? 'counsellor' : 'student'}</h2>
								<p>

							{modal === 'counselor'

								? 'Create a counsellor account and set their starting access.'

									: 'Enter the student’s details, then choose a counsellor if needed.'}

								</p>
							</div>
						</div>

						{modal === 'counselor' ? (

							<>

								<label>

									<span>Full name</span>

									<input

										autoFocus

										required

										value={form.name}

										onChange={e => setForm(x => ({ ...x, name: e.target.value }))}

										disabled={isCreatingCounselor}

									/>

								</label>

								<label>

									<span>Email</span>

									<input

										required

										type="email"

										value={form.email}

										onChange={e => setForm(x => ({ ...x, email: e.target.value }))}

										disabled={isCreatingCounselor}

										placeholder="counsellor@school.com"

									/>

								</label>

								<label>

									<span>Access level</span>

									<SelectDropdown
										value={form.access}
										onChange={value => setForm(x => ({ ...x, access: value }))}
										options={counselorAccessOptions}
										disabled={isCreatingCounselor}
										menuZIndex={3000}
									/>

								</label>

							</>

						) : (

							<AddStudentFields
								counselors={displayCounselors}
								counselorId={studentCounselorId}
								onCounselorChange={setStudentCounselorId}
								disabled={isLinkingStudent}
								search={addStudentSearch}
							/>

						)}

						<div className={s.dialogActions}>

							<button

								type="button"

								onClick={close}

								disabled={isLinkingStudent || isCreatingCounselor}

							>

								Cancel

							</button>

							<button

								type="submit"

								disabled={

									isLinkingStudent ||

									isCreatingCounselor ||

									(modal === 'student' && !addStudentSearch.canSubmit)

								}

							>

								{isLinkingStudent

									? 'Linking...'

								: isCreatingCounselor

									? 'Adding...'

									: <><PlusIcon aria-hidden />Add {modal === 'counselor' ? 'counsellor' : 'student'}</>}

							</button>

						</div>

					</form>

				</div>

			)}



			<Toast

				isVisible={showToast}

				message={toastMessage}

				onClose={() => setShowToast(false)}

			/>

		</main>

	)

})
